<template>
  <v-container fluid>
    <section>
      <KpiCards class="mb-3" :items="kpiItems" :loading="!summaries.length" :skeleton="4" />

      <!-- Clock In/Out Actions -->
      <v-card v-if="canManageShifts" class="mb-3" outlined>
        <v-card-text class="py-3 px-4">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-h6">{{ $t("shift_management") }}</div>
              <div v-if="activeShift">
                <div class="text-caption mb-1">
                  <v-icon x-small class="mr-1">fal fa-clock</v-icon>
                  {{ $t("clocked_in_at") }}:
                  {{ activeShift.clock_in_at | moment("DD/MM/YYYY HH:mm") }}
                </div>
                <div class="text-caption" v-if="activeShift.opening_cash">
                  <v-icon x-small class="mr-1">fal fa-money-bill</v-icon>
                  {{ $t("opening_cash") }}:
                  {{ activeShift.opening_cash | NumberFormat }}
                </div>
              </div>
              <div class="text-caption grey--text" v-else>
                {{ $t("no_active_shift") }}
              </div>
            </div>
            <v-btn
              v-if="!activeShift"
              depressed
              color="primary"
              class="rounded-lg"
              @click="showClockInDialog = true"
            >
              <v-icon left>fal fa-sign-in-alt</v-icon>
              {{ $t("clock_in") }}
            </v-btn>
            <v-btn
              v-else
              depressed
              outlined
              color="primary"
              class="rounded-lg"
              @click="showClockOutDialog = true"
            >
              <v-icon left>fal fa-sign-out-alt</v-icon>
              {{ $t("clock_out") }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Compact Filter Section -->
      <v-card class="mb-3" outlined>
        <v-card-text class="py-3 px-4">
          <div class="text-left">
            <h6>{{ $t("filter") }}</h6>
          </div>
          <!-- Main Filter Row -->
          <v-row align="center">
            <!-- Period Filter -->
            <v-col cols="12" sm="6" md="2" lg="2">
              <TextSelect
                :dense="true"
                :placeholder="false"
                :items="periodItems"
                label="period"
                itemValue="value"
                text="name"
                v-model="period"
                hide-details
              />
            </v-col>

            <!-- Start Date -->
            <v-col cols="12" sm="6" md="2" lg="2">
              <DatePicker
                :dense="true"
                :placeholder="false"
                label="start_date"
                v-model="date_from"
                hide-details
              />
            </v-col>

            <!-- End Date -->
            <v-col cols="12" sm="6" md="2" lg="2">
              <DatePicker
                :dense="true"
                :placeholder="false"
                label="end_date"
                v-model="date_to"
                hide-details
              />
            </v-col>

            <!-- Search Field -->
            <v-col cols="12" sm="6" md="3" lg="3" v-if="canViewAllShifts">
              <TextSearch
                v-model="search"
                @search="filterData()"
                @onClear="
                  search = '';
                  filterData();
                "
              />
            </v-col>

            <!-- Action Buttons -->
            <!-- <v-col cols="12" sm="6" md="3" lg="3">
              <div class="d-flex align-center justify-end">
                <v-btn
                  v-if="canApproveShifts"
                  depressed
                  color="primary"
                  @click="goToReports"
                >
                  <v-icon left small>fal fa-chart-line</v-icon>
                  {{ $t("reports") }}
                </v-btn>
              </div>
            </v-col> -->
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Data Table -->
      <data-table
        v-if="shiftsList && shiftsList.data"
        :headers="headers"
        :items="shiftsList"
        commit="SET_SHIFTS_LIST"
        :viewItem="viewShift"
        :customActions="getCustomActions"
      >
        <div slot="activator" class="row mb-4">
          <div class="col-12">
            <v-card-title class="pa-0 mb-0 ps-products-title">{{ $t("shifts") }} </v-card-title>
          </div>
        </div>
      </data-table>

      <!-- Dialogs -->
      <ClockInDialog
        v-model="showClockInDialog"
        @success="handleClockInSuccess"
      />
      <ClockOutDialog
        v-model="showClockOutDialog"
        :shift="activeShift"
        :forceLogout="forceLogout"
        @success="handleClockOutSuccess"
      />
      <ClockOutDetailDialog
        v-model="showClockOutDetailDialog"
        :shift="clockedOutShift"
      />
      <ShiftDetailDialog v-model="showDetailDialog" :shift="selectedShift" />
      <ReconciliationDialog
        v-model="showReconciliationDialog"
        :shift="selectedShift"
        @success="handleReconciliationSuccess"
      />
    </section>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import DataTable from "@/components/DataTable";
import TextSelect from "@/components/global/TextSelect";
import DatePicker from "@/components/global/DatePicker";
import TextSearch from "@/components/global/TextSearch";
import ClockInDialog from "./ClockInDialog";
import ClockOutDialog from "./ClockOutDialog";
import ClockOutDetailDialog from "./ClockOutDetailDialog";
import ShiftDetailDialog from "./ShiftDetailDialog";
import ReconciliationDialog from "./ReconciliationDialog";
import ShiftStatusBadge from "@/components/shift/ShiftStatusBadge";
import { shiftHead } from "@/utils/head";
import { myMixin } from "@/mixin/main";
import { confirm } from "@/controllers/alert";

export default {
  name: "ShiftManagement",
  mixins: [myMixin],
  components: {
    DataTable,
    TextSelect,
    DatePicker,
    TextSearch,
    ClockInDialog,
    ClockOutDialog,
    ClockOutDetailDialog,
    ShiftDetailDialog,
    ReconciliationDialog,
    ShiftStatusBadge,
  },
  data: () => ({
    showClockInDialog: false,
    showClockOutDialog: false,
    showClockOutDetailDialog: false,
    showDetailDialog: false,
    showReconciliationDialog: false,
    selectedShift: null,
    clockedOutShift: null,
    forceLogout: false,
    period: "monthly",
    date_from: new Date().toISOString().substr(0, 10),
    date_to: new Date().toISOString().substr(0, 10),
    periodItems: [
      { name: "Daily", value: "daily" },
      { name: "Weekly", value: "weekly" },
      { name: "Monthly", value: "monthly" },
    ],
    search: "",
    isInitialLoad: true,
    filterDebounceTimer: null,
  }),
  computed: {
    kpiItems() {
      return (this.summaries || []).map((item, index) => ({
        key: index,
        title: item.title,
        icon: item.icon,
        value: this.formatSummaryValue(item),
      }));
    },
    ...mapGetters({
      user: "auth/user",
      activeShift: "index/activeShift",
      shiftsList: "index/shiftsList",
      shiftStatistics: "index/shiftStatistics",
    }),
    summaries() {
      return [
        {
          title: "active_shifts",
          icon: "fas fa-clock",
          color: "blue",
          total: this.shiftStatistics.active_shifts || 0,
          type: "number",
        },
        {
          title: "pending_approval",
          icon: "fas fa-hourglass-half",
          color: "amber",
          total: this.shiftStatistics.pending_approval || 0,
          type: "number",
        },
        {
          title: "today_shifts",
          icon: "fas fa-calendar-check",
          color: "green",
          total: this.shiftStatistics.today_shifts || 0,
          type: "number",
        },
        {
          title: "total_variance",
          icon: "fas fa-chart-bar",
          color: "purple",
          total: this.shiftStatistics.total_variance || 0,
          type: "currency",
          isNegative: (this.shiftStatistics.total_variance || 0) < 0,
        },
      ];
    },
    headers() {
      return shiftHead(this);
    },
    canManageShifts() {
      // All non-admin roles can manage their own shifts
      return [2, 3, 4, 5].includes(this.user.role);
    },
    canViewAllShifts() {
      // Manager, Owner, Admin can view all shifts
      return [1, 2, 3].includes(this.user.role);
    },
    canApproveShifts() {
      // Manager, Owner, Admin can approve shifts
      return [1, 2, 3].includes(this.user.role);
    },
  },
  methods: {
    ...mapActions({
      fetchActiveShift: "index/fetchActiveShift",
      fetchShifts: "index/fetchShifts",
      fetchShiftStatistics: "index/fetchShiftStatistics",
      approveShift: "index/approveShift",
      logout: "auth/logout",
    }),
    getCustomActions(item) {
      const actions = [
        {
          icon: "fal fa-eye",
          color: "blue",
          tooltip: this.$t("view_details"),
          action: () => this.viewShift(item),
        },
      ];

      // Add approve action for Manager+ on pending shifts
      if (this.canApproveShifts && item.status === "pending_approval") {
        actions.push({
          icon: "fal fa-check-circle",
          color: "primary",
          tooltip: this.$t("approve_shift"),
          action: () => this.approveShiftAction(item),
        });
      }

      // Add reconciliation for Manager+
      if (
        this.canApproveShifts &&
        (item.status === "completed" || item.status === "pending_approval")
      ) {
        actions.push({
          icon: "fal fa-calculator",
          color: "primary",
          tooltip: this.$t("reconcile"),
          action: () => this.reconcileShift(item),
        });
      }

      return actions;
    },
    async applyFilter() {
      await this.fetchShifts({
        that: this,
        page: 1,
        filters: {
          period: this.period,
          date_from: this.date_from,
          date_to: this.date_to,
        },
      });
    },
    async handleClockInSuccess() {
      await this.fetchActiveShift(this);
      await this.fetchShiftStatistics(this);
      this.showClockInDialog = false;
    },
    async handleClockOutSuccess(shift) {
      this.showClockOutDialog = false;

      // If forceLogout is true, perform logout BEFORE fetching active shift
      // This prevents ShiftGuard from showing clock-in dialog during logout
      if (this.forceLogout) {
        await this.performForceLogout();
        return; // Exit early - logout clears all state
      }

      if (shift) {
        this.clockedOutShift = shift;
        this.showClockOutDetailDialog = true;
      }

      // Normal clock-out flow (no force logout)
      // Clear activeShift here to prevent ShiftGuard from showing clock-in dialog
      await this.$store.commit("index/SET_ACTIVE_SHIFT", null);
      await this.fetchShifts({ that: this, page: 1 });
      await this.fetchShiftStatistics(this);
    },
    viewShift(shift) {
      this.selectedShift = shift;
      this.showDetailDialog = true;
    },
    async approveShiftAction(shift) {
      const confirmed = await confirm({
        props: this,
        title: "approve_shift",
        text: "approve_shift_confirm",
        con_txt: "approve",
        can_txt: "cancel",
        color_con: "#1976D2",
      });

      if (confirmed.isConfirmed) {
        try {
          await this.approveShift({
            that: this,
            shiftId: shift.id,
            approved: true,
          });
          this.$toast.success(this.$t("shift_approved"));
          await this.fetchShifts({ that: this, page: 1 });
          await this.fetchShiftStatistics(this);
        } catch (error) {
          this.$toast.error(
            error.response?.data?.message || this.$t("error_occurred")
          );
        }
      }
    },
    reconcileShift(shift) {
      this.selectedShift = shift;
      this.showReconciliationDialog = true;
    },
    async handleReconciliationSuccess() {
      await this.fetchShifts({ that: this, page: 1 });
      this.showReconciliationDialog = false;
    },
    goToReports() {
      this.$router.push({ name: "shift_reports" });
    },

    // Perform forced logout after clock out
    async performForceLogout() {
      try {
        await this.logout(this);
      } catch (error) {
        console.error("Force logout error:", error);
      }
    },

    // Check for forceClockOut query parameter and auto-open clock-out dialog
    checkForceClockOut() {
      const { forceClockOut } = this.$route.query;
      if (forceClockOut === "true" || forceClockOut === true) {
        if (this.activeShift) {
          // Set forceLogout flag to true so clock-out dialog knows to logout after success
          this.forceLogout = true;
          // Open clock-out dialog automatically
          this.showClockOutDialog = true;
        } else {
          // No active shift, just logout directly
          this.performForceLogout();
        }
      }
    },

    // Format summary value based on type
    formatSummaryValue(item) {
      if (item.type === "currency") {
        return this.$options.filters.NumberFormat(item.total);
      }
      return item.total;
    },

    // Filter data method for search
    async filterData() {
      await this.applyFilter();
    },
  },
  watch: {
    date_from() {
      this.applyFilter();
    },
    date_to() {
      this.applyFilter();
    },
    period() {
      this.applyFilter();
    },
  },
  beforeDestroy() {
    // Clear debounce timer on component destroy
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }
  },
  async created() {
    // Shift check is now handled globally by ShiftGuard component in layouts

    await this.fetchActiveShift(this);
    await this.fetchShiftStatistics(this);

    // Only fetch shifts if user can view them
    if (this.canViewAllShifts || this.activeShift) {
      await this.fetchShifts({ that: this, page: 1 });
    }

    // Check for forceClockOut query parameter
    this.checkForceClockOut();

    // Mark initial load as complete after a short delay
    this.$nextTick(() => {
      setTimeout(() => {
        this.isInitialLoad = false;
      }, 100);
    });
  },
};
</script>

<style scoped>
.ps-card-soft {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  box-shadow: none !important;
  transition: box-shadow 0.15s ease;
}

.ps-products-title {
  font-size: 1.25rem;
  font-weight: 900;
  color: #141021;
}

.ps-summary-icon {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
}
</style>
